import type { Notebook, Tag } from "@engram/core";
import { parseTagIds } from "@engram/core/id";
import {
  ArrowDownUp, Bookmark, Check, CopyPlus, Download, ExternalLink, FolderInput, Link2, ListFilter, Lock, Merge, PanelTop, MoreHorizontal, Pin, RotateCcw, Search, SquarePen, Star,
  Tag as TagIcon, Trash2,
} from "lucide-react";
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import {
  addShortcut, createNote, deleteNotebook, deleteSavedSearch, duplicateNote, emptyTrash, removeShortcut, renameNotebook, restoreNotes, saveSearch, trashNotes,
  updateNote, updateSavedSearch, notebookNameError, moveNotebookToStack, setSetting,
} from "../db/actions";
import { useQuery } from "../db/client";
import { useThumbnailUrl } from "./attachments";
import { BulkOpView, SelectionBar, type BulkOp } from "./Bulk";
import { groupKey, plural, relativeDate, shortDate, dateFmt } from "./format";
import { ExportDialog } from "./ExportLazy";
import { StackPrompt } from "./NotebooksPage";
import { queueFilesForNote } from "./editor/Editor";
import { useLayout } from "./layout";
import { openInTab } from "./Tabs";
import { FilterChips, FilterPopover } from "./ListFilters";
import { OtherShortcuts, ShortcutsDialog } from "./Shortcuts";
import { rememberSearch } from "./Quick";
import { Confirm, Menu, Popover, Prompt, pointAnchor, useAnchor, type Anchor } from "./Menu";
import { usePrefs, type SortKey, type ViewMode } from "./prefs";
import { filterCount, type ListContext, type ListFilters, type NoteRow } from "./queries";

export interface ClickMods {
  toggle: boolean; // Ctrl/Cmd-click, or a tap while selecting on a phone
  range: boolean; // Shift-click
}

/**
 * Where each list was scrolled to, so coming back to it keeps your place like Evernote: a phone's back button from a
 * note (the list isn't drawn while a note fills the screen) or Notes → Home → Notes. A new list starts at the top.
 */
const scrollMemory = new Map<string, number>();

export function NoteList({
  ctx, title, notes, ids, loading = false, onMore, selected, multi, selecting, onPick, onLongPress, onClearMulti, onSelectAll, filters, onFilters, savedId, view, hitKey = "",
}: {
  ctx: ListContext;
  title: string;
  /** Search terms as a JSON array string (so memoized cards compare cheaply); highlighted in snippets. */
  hitKey?: string;
  /** Full rows for the cards to draw (the start of the list). */
  notes: NoteRow[] | undefined;
  /** Every note id in the list, in order. */
  ids: string[] | undefined;
  /** A new list (another notebook, search, filter) is still being read; what's shown is the previous one. */
  loading?: boolean;
  /** Asks for more rows (the end of the drawn cards came near). */
  onMore: () => void;
  selected: string | null;
  multi: string[];
  selecting: boolean; // phone selection mode
  onPick: (id: string, mods: ClickMods) => void;
  onLongPress: (id: string) => void;
  onClearMulti: () => void;
  onSelectAll: () => void;
  filters: ListFilters;
  onFilters: (f: ListFilters) => void;
  savedId: string | null;
  view: ViewMode;
}) {
  const prefs = usePrefs();
  const trash = ctx.kind === "trash";
  const sortKey: SortKey = trash ? prefs.trashSort : prefs.sort === "deleted" ? "updated" : prefs.sort;
  const sortDesc = trash ? prefs.trashSortDesc : prefs.sortDesc;
  useEffect(() => {
    if (notes && !performance.getEntriesByName("engram:list").length) performance.mark("engram:list");
  }, [notes]);
  const sortMenu = useAnchor();
  const moreMenu = useAnchor();
  const filterMenu = useAnchor();
  const [, navigate] = useLocation();
  const [dialog, setDialog] = useState<null | "rename" | "delete" | "empty" | "save" | "export" | "renameSearch" | "editSearch" | "deleteSearch" | "shortcuts" | "stack">(null);
  const [ctxMenu, setCtxMenu] = useState<{ id: string; anchor: Anchor } | null>(null);
  const [dropping, setDropping] = useState(false);
  // NotesPane loads rows for the first cards and more as the list is scrolled toward the end (a sentinel near the
  // bottom asks for them). Building every card up front made leaving and returning to a ~1,200-note list cost
  // ~1.3 s on a slow phone (6× CPU, chat 9).
  const shown = notes;
  const total = ids?.length ?? 0;
  const scrollRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const hasMore = !!shown && shown.length < total;
  useEffect(() => {
    const el = moreRef.current;
    if (!hasMore || !el) return;
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && onMore(), {
      root: scrollRef.current,
      rootMargin: "0px 0px 1500px 0px",
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, onMore, shown?.length]);
  const memKey = `${JSON.stringify(ctx)}|${savedId ?? ""}|${view}`;
  const restored = useRef("");
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !shown?.length || restored.current === memKey) return;
    restored.current = memKey;
    el.scrollTop = scrollMemory.get(memKey) ?? 0;
  }, [memKey, shown]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const save = () => scrollMemory.set(memKey, el.scrollTop);
    el.addEventListener("scroll", save, { passive: true });
    return () => el.removeEventListener("scroll", save);
  }, [memKey]);
  const tags = useQuery<Tag>(prefs.showTags || view === "top" ? "SELECT * FROM tags WHERE deleted = 0" : null, [], ["tags"]);
  const tagName = useMemo(() => new Map(tags?.map((t) => [t.id, t.name])), [tags]);
  const starRows = useQuery<{ target_id: string }>("SELECT target_id FROM shortcuts WHERE kind = 'note' AND deleted = 0", [], ["shortcuts"]);
  const starred = useMemo(() => new Set(starRows?.map((r) => r.target_id)), [starRows]);
  const onContext = useCallback((id: string, anchor: Anchor) => setCtxMenu({ id, anchor }), []);
  const targetId = "id" in ctx ? ctx.id : savedId;
  const shortcut = useQuery<{ id: string }>(
    ctx.kind === "notebook" || ctx.kind === "stack" || ctx.kind === "tag" || savedId ? "SELECT id FROM shortcuts WHERE target_id = ? AND deleted = 0" : null,
    [targetId ?? ""],
    ["shortcuts"],
  );
  // Evernote won't delete the default notebook (new notes go there).
  const defaultNb = useQuery<{ value: string }>("SELECT value FROM settings WHERE id = 'defaultNotebook' AND deleted = 0", [], ["settings"])?.[0];
  const isDefaultNb = ctx.kind === "notebook" && !!defaultNb && JSON.parse(defaultNb.value) === ctx.id;
  const isShortcut = !!shortcut?.length;
  const notebooks = useQuery<Notebook>(ctx.kind === "notebook" ? "SELECT * FROM notebooks WHERE deleted = 0" : null, [], ["notebooks"]);
  const thisNb = ctx.kind === "notebook" ? notebooks?.find((n) => n.id === ctx.id) : undefined;
  const multiSet = new Set(multi);
  const nFilters = filterCount(filters);

  const onListKey = (e: React.KeyboardEvent) => {
    if (!ids?.length || (e.target as HTMLElement).closest("input, textarea")) return;
    const i = selected ? ids.indexOf(selected) : -1;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = ids[Math.max(0, Math.min(ids.length - 1, i + (e.key === "ArrowDown" ? 1 : -1)))];
      if (next) {
        onPick(next, { toggle: false, range: false });
        // The card may be drawn only once its rows load (moving past the drawn end).
        const focus = (tries: number) => {
          const el = document.querySelector<HTMLElement>(`[data-note-id="${next}"]`);
          if (el) el.focus();
          else if (tries > 0) setTimeout(() => focus(tries - 1), 50);
        };
        requestAnimationFrame(() => focus(10));
      }
    } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
      e.preventDefault();
      onSelectAll();
    } else if (e.key === "Escape" && multi.length) {
      onClearMulti();
    } else if (e.key === "Enter" && !e.ctrlKey && !e.altKey && !e.shiftKey && !selecting) {
      // Evernote: Enter in the list puts the cursor in the note.
      const id = (e.target as HTMLElement).closest<HTMLElement>("[data-note-id]")?.dataset.noteId;
      if (!id) return;
      e.preventDefault();
      if (id !== selected) onPick(id, { toggle: false, range: false });
      setTimeout(() => window.dispatchEvent(new Event("engram:focus-editor")), id === selected ? 0 : 150);
    } else if (e.key === "Delete" && ctx.kind !== "trash") {
      // Evernote: Delete moves the selected note(s) to the Trash.
      const gone = multi.length ? multi : selected ? [selected] : [];
      if (gone.length) {
        e.preventDefault();
        // Open the next note (or the previous one at the end), like Evernote, so the list keeps a selection.
        const out = new Set(gone);
        const at = selected && ids ? ids.indexOf(selected) : -1;
        const next = at >= 0 && ids ? (ids.slice(at + 1).find((x) => !out.has(x)) ?? ids.slice(0, at).reverse().find((x) => !out.has(x))) : undefined;
        void trashNotes(gone);
        onClearMulti();
        if (next) {
          onPick(next, { toggle: false, range: false });
          requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-note-id="${next}"]`)?.focus());
        }
      }
    }
  };

  const cardProps = { onPick, onLongPress, selecting, showImages: prefs.showImages, showBody: prefs.showBody, tagNames: prefs.showTags ? tagName : null, onContext, hitKey };

  return (
    <section className={`note-list view-${view}`}>
      {selecting ? (
        <SelectionBar ids={multi} trash={ctx.kind === "trash"} onClear={onClearMulti} onSelectAll={onSelectAll} />
      ) : (
        <header className="list-header">
          <div className="list-title-row">
            <h1 className="list-title" title={title}>
              <span className="list-title-text">{title}</span>
              {ids && !loading && ctx.kind !== "search" && <span className="title-count">{ids.length}</span>}
            </h1>
            <div className="list-actions">
              {ctx.kind !== "trash" && (
                <button
                  className="icon-btn"
                  title="New note"
                  onClick={async () => {
                    const id = await createNote(ctx.kind === "notebook" ? { notebookId: ctx.id } : ctx.kind === "tag" ? { tagIds: [ctx.id] } : {});
                    onPick(id, { toggle: false, range: false });
                  }}
                >
                  <SquarePen size={16} className="ic-create" />
                </button>
              )}
              <button className={`icon-btn${nFilters ? " on" : ""}`} title="Filter notes" onClick={filterMenu.open}>
                <ListFilter size={16} />
                {nFilters > 0 && <span className="badge">{nFilters}</span>}
              </button>
              <button className="icon-btn" title="Sort and view options" onClick={sortMenu.open}>
                <ArrowDownUp size={16} />
              </button>
              {ctx.kind === "trash" && (
                <button className="link-btn" disabled={!total} onClick={() => setDialog("empty")}>Empty Trash</button>
              )}
              {ctx.kind === "search" && !savedId && (
                <button className="link-btn save-search" onClick={() => setDialog("save")}>Save search</button>
              )}
              {(ctx.kind === "notebook" || ctx.kind === "stack" || ctx.kind === "tag" || (ctx.kind === "search" && savedId) || ctx.kind === "shortcuts") && (
                <button className="icon-btn" title="More actions" onClick={moreMenu.open}>
                  <MoreHorizontal size={16} />
                </button>
              )}
            </div>
          </div>
          {ctx.kind === "search" && !savedId && <SearchBox key={ctx.q} q={ctx.q} />}
          {ctx.kind === "search" && savedId && <div className="list-query" title={ctx.q}>{ctx.q}</div>}
          {ctx.kind === "search" && <div className="list-count">{!ids || loading ? "Searching…" : total ? `${plural(total, "note")} found` : "No notes found"}</div>}
          <FilterChips filters={filters} onChange={onFilters} />
        </header>
      )}

      <div
        ref={scrollRef}
        className={`list-scroll${dropping ? " dropping" : ""}`}
        aria-busy={loading || undefined}
        role="listbox"
        aria-label="Notes"
        aria-multiselectable
        onKeyDown={onListKey}
        // Evernote: drop files on the list to make a note with them as attachments.
        onDragOver={(e) => {
          if (ctx.kind === "trash" || !e.dataTransfer.types.includes("Files")) return;
          e.preventDefault();
          setDropping(true);
        }}
        onDragLeave={(e) => !e.currentTarget.contains(e.relatedTarget as Node) && setDropping(false)}
        onDrop={async (e) => {
          if (!dropping) return;
          e.preventDefault();
          setDropping(false);
          const files = [...e.dataTransfer.files];
          if (!files.length) return;
          const id = await createNote({
            title: files.length === 1 ? files[0]!.name.replace(/\.[^.]+$/, "") : "",
            ...(ctx.kind === "notebook" ? { notebookId: ctx.id } : ctx.kind === "tag" ? { tagIds: [ctx.id] } : {}),
          });
          queueFilesForNote(id, files);
          onPick(id, { toggle: false, range: false });
        }}
      >
        {dropping && <div className="drop-hint">Drop files to create a note with attachments</div>}
        {ctx.kind === "shortcuts" && !selecting && <OtherShortcuts />}
        {view === "top" ? (
          <TopTable {...cardProps} notes={shown} selected={selected} multi={multiSet} starred={starred} tagNames={tagName} />
        ) : (
          shown?.map((n, i) => {
            const group = prefs.showGroups ? groupLabel(n, sortKey, ctx.kind === "notebook") : null;
            const card = <NoteCard key={n.id} note={n} selected={n.id === selected && !multi.length} checked={multiSet.has(n.id)} starred={starred.has(n.id)} view={view} {...cardProps} />;
            if (group == null || (i > 0 && groupLabel(shown[i - 1]!, sortKey, ctx.kind === "notebook") === group)) return card;
            return [<div key={`g:${group}`} className="list-group" role="presentation">{group}</div>, card];
          })
        )}
        {hasMore && <div ref={moreRef} className="list-more" aria-hidden />}
        {ids?.length === 0 && !loading && <EmptyList ctx={ctx} filtered={nFilters > 0} />}
      </div>

      {filterMenu.anchor && <FilterPopover anchor={filterMenu.anchor} ctx={ctx} filters={filters} onChange={onFilters} onClose={filterMenu.close} />}

      {sortMenu.anchor && (
        <Popover anchor={sortMenu.anchor} onClose={sortMenu.close} align="end" className="menu sort-menu">
          <div className="menu-heading">Sort by</div>
          {(
            [
              ["title", "Title"],
              ["updated", "Date updated"],
              ["created", "Date created"],
              ...(trash ? [["deleted", "Date deleted"]] : []),
            ] as [SortKey, string][]
          ).map(([k, label]) => (
            <button
              key={k}
              className="menu-item"
              onClick={() =>
                trash
                  ? prefs.set(sortKey === k ? { trashSortDesc: !sortDesc } : { trashSort: k, trashSortDesc: k !== "title" })
                  : prefs.set(sortKey === k ? { sortDesc: !sortDesc } : { sort: k, sortDesc: k !== "title" })
              }
            >
              <span className="menu-icon">{sortKey === k && <Check size={14} />}</span>
              <span className="menu-label">{label}</span>
              {sortKey === k && <span className="menu-hint">{k === "title" ? (sortDesc ? "Z–A" : "A–Z") : sortDesc ? "Newest" : "Oldest"}</span>}
            </button>
          ))}
          <div className="menu-sep" />
          <div className="menu-heading">View as</div>
          {(
            [
              ["snippets", "Snippets"],
              ["cards", "Cards"],
              ["side", "Side list"],
              ["top", "Top list"],
            ] as [ViewMode, string][]
          ).map(([k, label]) => (
            <button key={k} className="menu-item" onClick={() => prefs.set({ view: k })}>
              <span className="menu-icon">{prefs.view === k && <Check size={14} />}</span>
              <span className="menu-label">{label}</span>
            </button>
          ))}
          <div className="menu-sep" />
          <div className="menu-heading">Show in preview</div>
          {(
            [
              ["showImages", "Images"],
              ["showBody", "Body text"],
              ["showTags", "Tags"],
            ] as const
          ).map(([k, label]) => (
            <button key={k} className="menu-item" onClick={() => prefs.set({ [k]: !prefs[k] })}>
              <span className="menu-icon">{prefs[k] && <Check size={14} />}</span>
              <span className="menu-label">{label}</span>
            </button>
          ))}
          <div className="menu-sep" />
          <button className="menu-item" disabled={view === "top"} onClick={() => prefs.set({ showGroups: !prefs.showGroups })}>
            <span className="menu-icon">{prefs.showGroups && <Check size={14} />}</span>
            <span className="menu-label">Show in groups</span>
          </button>
        </Popover>
      )}

      {moreMenu.anchor && (
        <Menu
          anchor={moreMenu.anchor}
          align="end"
          onClose={moreMenu.close}
          entries={
            ctx.kind === "trash"
              ? [{ label: "Empty trash", icon: <Trash2 size={14} />, danger: true, disabled: !total, onSelect: () => setDialog("empty") }]
              : ctx.kind === "shortcuts"
                ? [{ label: "Reorder shortcuts…", icon: <ArrowDownUp size={14} />, onSelect: () => setDialog("shortcuts") }]
              : ctx.kind === "search" && savedId
                ? [
                    {
                      label: isShortcut ? "Remove from Shortcuts" : "Add to Shortcuts",
                      icon: <Star size={14} />,
                      onSelect: () => void (isShortcut ? removeShortcut("search", savedId) : addShortcut("search", savedId)),
                    },
                    { label: "Rename saved search", onSelect: () => setDialog("renameSearch") },
                    { label: "Edit search query", icon: <Search size={14} />, onSelect: () => setDialog("editSearch") },
                    { separator: true as const },
                    { label: "Delete saved search", icon: <Trash2 size={14} />, danger: true, onSelect: () => setDialog("deleteSearch") },
                  ]
              : ctx.kind === "search"
                ? [{ label: "Save search", icon: <Bookmark size={14} />, onSelect: () => setDialog("save") }]
                : ctx.kind === "notebook"
                  ? [
                      // Evernote's order for a notebook.
                      {
                        label: "Add new note",
                        icon: <SquarePen size={14} />,
                        onSelect: async () => onPick(await createNote({ notebookId: ctx.id }), { toggle: false, range: false }),
                      },
                      { label: "Rename notebook", onSelect: () => setDialog("rename") },
                      { label: "Delete notebook", danger: true, icon: <Trash2 size={14} />, disabled: isDefaultNb, hint: isDefaultNb ? "Default notebook" : undefined, onSelect: () => setDialog("delete") },
                      { separator: true as const },
                      { label: isShortcut ? "Remove from Shortcuts" : "Add to Shortcuts", icon: <Star size={14} />, onSelect: () => void (isShortcut ? removeShortcut("notebook", ctx.id) : addShortcut("notebook", ctx.id)) },
                      { label: "Set as default notebook", disabled: isDefaultNb, onSelect: () => void setSetting("defaultNotebook", ctx.id) },
                      { label: thisNb?.parent_id ? "Move to another stack…" : "Add to stack…", onSelect: () => setDialog("stack") },
                      ...(thisNb?.parent_id ? [{ label: "Remove from stack", onSelect: () => void moveNotebookToStack(ctx.id, null) }] : []),
                      { label: "Export notebook…", icon: <Download size={14} />, onSelect: () => setDialog("export") },
                    ]
                : [
                    {
                      label: isShortcut ? "Remove from Shortcuts" : "Add to Shortcuts",
                      icon: <Star size={14} />,
                      onSelect: () => ("id" in ctx ? (isShortcut ? removeShortcut(ctx.kind, ctx.id) : addShortcut(ctx.kind as "stack", ctx.id)) : undefined),
                    },
                    ...(ctx.kind === "stack"
                      ? [
                          { label: "Rename stack", onSelect: () => setDialog("rename") },
                          { separator: true as const },
                          { label: "Delete stack", danger: true, icon: <Trash2 size={14} />, onSelect: () => setDialog("delete") },
                        ]
                      : []),
                  ]
          }
        />
      )}

      {ctxMenu && (
        <NoteContextMenu
          id={ctxMenu.id}
          anchor={ctxMenu.anchor}
          ids={multiSet.has(ctxMenu.id) ? multi : [ctxMenu.id]}
          note={notes?.find((n) => n.id === ctxMenu.id)}
          trash={ctx.kind === "trash"}
          onClose={() => setCtxMenu(null)}
          onDone={onClearMulti}
        />
      )}

      {dialog === "export" && ctx.kind === "notebook" && <ExportDialog scope={{ kind: "notebook", id: ctx.id, name: title }} onClose={() => setDialog(null)} />}
      {dialog === "rename" && "id" in ctx && <Prompt title={`Rename ${ctx.kind}`} initial={title} validate={notebookNameError(ctx.kind === "stack" ? "stack" : "notebook", ctx.id)} onSubmit={(v) => renameNotebook(ctx.id, v)} onClose={() => setDialog(null)} />}
      {dialog === "delete" && "id" in ctx && (
        <Confirm
          title={`Delete ${ctx.kind}?`}
          body={ctx.kind === "stack" ? "The notebooks in this stack are kept." : `${plural(total, "note")} in "${title}" will move to the Trash.`}
          confirm="Delete"
          danger
          onConfirm={async () => {
            await deleteNotebook(ctx.id);
            navigate("/notes");
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "empty" && (
        <Confirm title="Empty trash?" body={`${plural(total, "note")} will be permanently deleted. This can't be undone.`} confirm="Empty trash" danger onConfirm={() => void emptyTrash()} onClose={() => setDialog(null)} />
      )}
      {dialog === "shortcuts" && <ShortcutsDialog onClose={() => setDialog(null)} />}
      {dialog === "stack" && thisNb && <StackPrompt notebook={thisNb} stacks={notebooks?.filter((n) => n.kind === "stack") ?? []} onClose={() => setDialog(null)} />}
      {dialog === "renameSearch" && savedId && <Prompt title="Rename saved search" initial={title} onSubmit={(v) => void updateSavedSearch(savedId, { name: v })} onClose={() => setDialog(null)} />}
      {dialog === "editSearch" && savedId && ctx.kind === "search" && (
        <Prompt title="Edit saved search" label={'Evernote search syntax, e.g. tag:work "exact phrase"'} initial={ctx.q} onSubmit={(v) => void updateSavedSearch(savedId, { query: v })} onClose={() => setDialog(null)} />
      )}
      {dialog === "deleteSearch" && savedId && (
        <Confirm
          title="Delete saved search?"
          body={`"${title}" will be removed. Your notes are not affected.`}
          confirm="Delete"
          danger
          onConfirm={async () => {
            await deleteSavedSearch(savedId);
            navigate("/notes");
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "save" && ctx.kind === "search" && (
        <Prompt
          title="Save search"
          label={`Query: ${ctx.q}`}
          initial={ctx.q}
          onSubmit={async (name) => {
            const id = await saveSearch(name, ctx.q);
            await addShortcut("search", id);
          }}
          onClose={() => setDialog(null)}
        />
      )}
    </section>
  );
}

/** Right-click on a note (or on a selection of notes). */
function NoteContextMenu({ id, anchor, ids, note, trash, onClose, onDone }: { id: string; anchor: Anchor; ids: string[]; note?: NoteRow; trash: boolean; onClose: () => void; onDone: () => void }) {
  const [op, setOp] = useState<BulkOp | null>(null);
  const [, navigate] = useLocation();
  const layout = useLayout();
  const many = ids.length > 1;
  if (op) return <BulkOpView op={op} anchor={anchor} ids={ids} onClose={onClose} onDone={onDone} />;
  return (
    <Menu
      anchor={anchor}
      onClose={() => !op && onClose()}
      entries={
        trash
          ? [
              { heading: many ? `${ids.length} notes` : note?.title || "Untitled" },
              { label: "Restore", icon: <RotateCcw size={14} />, onSelect: () => void restoreNotes(ids).then(onDone) },
              { label: "Delete permanently", icon: <Trash2 size={14} />, danger: true, onSelect: () => setOp("delete") },
            ]
          : [
              ...(many
                ? [{ heading: `${ids.length} notes` }]
                : [
                    ...(layout === "wide" ? [{ label: "Open in new tab", icon: <PanelTop size={14} />, onSelect: () => openInTab(id) }] : []),
                    { label: "Open in new window", icon: <ExternalLink size={14} />, onSelect: () => window.open(`/note/${id}`, "_blank") },
                    { label: "Copy link", icon: <Link2 size={14} />, onSelect: () => void navigator.clipboard.writeText(`${location.origin}/note/${id}`) },
                    { separator: true as const },
                  ]),
              { label: "Move…", icon: <FolderInput size={14} />, onSelect: () => setOp("move") },
              { label: "Copy to…", icon: <CopyPlus size={14} />, onSelect: () => setOp("copy") },
              ...(many ? [] : [{ label: "Duplicate", icon: <CopyPlus size={14} />, onSelect: async () => { const n = await duplicateNote(id); if (n) navigate(`/note/${n}`); } }]),
              { label: "Tags…", icon: <TagIcon size={14} />, onSelect: () => setOp("tags") },
              { label: "Add to Shortcuts", icon: <Star size={14} />, onSelect: () => void Promise.all(ids.map((x) => addShortcut("note", x))) },
              ...(many || !note ? [] : [{ label: note.pinned_at ? "Unpin from notebook" : "Pin to notebook", icon: <Pin size={14} />, onSelect: () => void updateNote(id, { pinned_at: note.pinned_at ? null : Date.now() }) }]),
              ...(many ? [{ label: "Merge…", icon: <Merge size={14} />, onSelect: () => setOp("merge") }] : []),
              { label: "Export…", icon: <Download size={14} />, onSelect: () => setOp("export") },
              { separator: true as const },
              { label: "Move to Trash", icon: <Trash2 size={14} />, danger: true, hint: "Del", onSelect: () => void trashNotes(ids).then(onDone) },
            ]
      }
    />
  );
}

/** Evernote's "Show in groups": months when sorted by date, initials (A–Z, Hangul) by title. */
function groupLabel(n: NoteRow, sort: SortKey, pinnedFirst: boolean): string {
  if (pinnedFirst && n.pinned_at != null) return "Pinned";
  if (sort === "title") return groupKey(n.title || "#");
  const d = new Date(sort === "created" ? n.created : sort === "deleted" ? (n.trashed_at ?? n.updated) : n.updated);
  const now = new Date();
  return dateFmt(d.getFullYear() === now.getFullYear() ? { month: "long" } : { month: "long", year: "numeric" }).format(d);
}

function SearchBox({ q }: { q: string }) {
  const [v, setV] = useState(q);
  const [, navigate] = useLocation();
  const focus = new URLSearchParams(location.search).get("focus") === "1";
  return (
    <form
      className="find-input list-search"
      onSubmit={(e) => {
        e.preventDefault();
        rememberSearch(v);
        navigate(`/search?q=${encodeURIComponent(v.trim())}`, { replace: true });
      }}
    >
      <Search size={14} />
      <input autoFocus={focus} value={v} onChange={(e) => setV(e.target.value)} placeholder='Search, e.g. tag:work "exact phrase"' enterKeyHint="search" />
    </form>
  );
}

function EmptyList({ ctx, filtered }: { ctx: ListContext; filtered: boolean }) {
  const msg =
    filtered ? "No notes match these filters" :
    ctx.kind === "trash" ? "Trash is empty" :
    ctx.kind === "search" ? "No notes match your search" :
    ctx.kind === "shortcuts" ? "No note shortcuts yet" :
    "No notes yet";
  return <div className="list-empty">{msg}</div>;
}

interface CardEvents {
  onPick: (id: string, mods: ClickMods) => void;
  onLongPress: (id: string) => void;
  onContext: (id: string, anchor: Anchor) => void;
  selecting: boolean;
}

/** Click, Ctrl/Shift-click, long-press (phone) and right-click, shared by cards and table rows. */
function useCardEvents(id: string, { onPick, onLongPress, onContext, selecting }: CardEvents) {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const longPressed = useRef(false);
  const cancel = () => clearTimeout(timer.current);
  return {
    "data-note-id": id,
    tabIndex: 0,
    onAuxClick: (e: React.MouseEvent) => {
      if (e.button !== 1 || selecting) return;
      e.preventDefault();
      openInTab(id);
    },
    onClick: (e: React.MouseEvent) => {
      if (longPressed.current) {
        longPressed.current = false;
        return;
      }
      onPick(id, { toggle: e.ctrlKey || e.metaKey || selecting, range: e.shiftKey });
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (selecting && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onPick(id, { toggle: true, range: false });
      }
    },
    onTouchStart: () => {
      longPressed.current = false;
      cancel();
      timer.current = setTimeout(() => {
        longPressed.current = true;
        navigator.vibrate?.(15);
        onLongPress(id);
      }, 450);
    },
    onTouchMove: cancel,
    onTouchEnd: cancel,
    onContextMenu: (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      // Touch long-press also fires contextmenu; that one is handled by the timer.
      if (longPressed.current || (e.nativeEvent as PointerEvent).pointerType === "touch") return;
      onContext(id, pointAnchor(e.clientX, e.clientY));
    },
  };
}

type CardProps = { note: NoteRow; selected: boolean; checked: boolean; starred: boolean; view: ViewMode; showImages: boolean; showBody: boolean; tagNames: Map<string, string> | null; hitKey: string } & CardEvents;

/** Search hits in a snippet, marked like Evernote's yellow highlights. */
function Highlighted({ text, hitKey }: { text: string; hitKey: string }) {
  const terms: string[] = hitKey ? JSON.parse(hitKey) : [];
  if (!terms.length) return <>{text}</>;
  // A snippet cut before the hit starts at a word, not mid-word.
  if (text.startsWith("…")) text = "…" + text.slice(1).replace(/^\S{1,20}\s+/, "");
  const re = new RegExp(`(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
  return <>{text.split(re).map((part, i) => (i % 2 ? <mark key={i} className="search-hit">{part}</mark> : part))}</>;
}

const NoteCard = memo(function NoteCard({ note, selected, checked, starred, view, showImages, showBody, tagNames, hitKey, ...events }: CardProps) {
  const thumb = useThumbnailUrl(showImages && view !== "side" ? note.thumb : null);
  const tagIds: string[] = tagNames ? parseTagIds(note.tag_ids) : [];
  const handlers = useCardEvents(note.id, events);
  return (
    <div role="option" aria-selected={selected || checked} className={`note-card${selected ? " selected" : ""}${checked ? " checked" : ""}`} {...handlers}>
      {events.selecting && <span className={`select-box${checked ? " on" : ""}`} aria-hidden>{checked && <Check size={12} strokeWidth={3} />}</span>}
      <div className="note-card-main">
        <div className="note-card-title">
          <span className="t">
            {note.pinned_at != null && <Pin size={11} className="pin" />}
            {note.title || "Untitled"}
          </span>
          {/* Evernote marks shortcut notes with a small yellow star after the title. */}
          {starred && <Star size={12} className="star" aria-label="Shortcut" />}
          {note.locked_at != null && <Lock size={11} className="lock" aria-label="Locked" />}
        </div>
        {showBody && view !== "side" && (
          <div className="note-card-snippet">
            <Highlighted text={note.snippet} hitKey={hitKey} />
          </div>
        )}
        {tagIds.length > 0 && (
          <div className="note-card-tags">
            {tagIds.map((t) => tagNames?.get(t) && <span key={t} className="chip small">{tagNames.get(t)}</span>)}
          </div>
        )}
        <div className="note-card-date">{relativeDate(note.updated)}</div>
      </div>
      {thumb && <img className="note-card-thumb" src={thumb} alt="" loading="lazy" />}
    </div>
  );
}, sameCard);

/** Query results are fresh objects on every refresh; compare what the card shows so typing in a note doesn't re-render the whole list. */
function sameCard(a: CardProps, b: CardProps): boolean {
  for (const k of Object.keys(a) as (keyof typeof a)[]) {
    if (k === "note") continue;
    if (a[k] !== b[k]) return false;
  }
  const x = a.note;
  const y = b.note;
  return x.id === y.id && x.title === y.title && x.snippet === y.snippet && x.updated === y.updated && x.pinned_at === y.pinned_at && x.locked_at === y.locked_at && x.thumb === y.thumb && x.tag_ids === y.tag_ids;
}

/** Evernote's "Top list": a sortable table above the note. */
function TopTable({ notes, selected, multi, starred, tagNames, ...events }: { notes: NoteRow[] | undefined; selected: string | null; multi: Set<string>; starred: Set<string>; tagNames: Map<string, string> } & CardEvents & { showImages: boolean; showBody: boolean }) {
  const prefs = usePrefs();
  const notebooks = useQuery<Notebook>("SELECT id, name FROM notebooks WHERE deleted = 0", [], ["notebooks"]);
  const nbName = new Map(notebooks?.map((n) => [n.id, n.name]));
  const head = (k: SortKey, label: string) => (
    <th className={`sortable${prefs.sort === k ? " on" : ""}`} onClick={() => prefs.set(prefs.sort === k ? { sortDesc: !prefs.sortDesc } : { sort: k, sortDesc: k !== "title" })}>
      {label}
      {prefs.sort === k && <span className="sort-dir">{prefs.sortDesc ? "↓" : "↑"}</span>}
    </th>
  );
  return (
    <table className="top-table">
      <thead>
        <tr>
          {head("title", "Title")}
          {head("updated", "Updated")}
          {head("created", "Created")}
          <th>Notebook</th>
          <th>Tags</th>
        </tr>
      </thead>
      <tbody>
        {notes?.map((n) => (
          <TopRow key={n.id} note={n} selected={n.id === selected && !multi.size} checked={multi.has(n.id)} starred={starred.has(n.id)} notebook={nbName.get(n.notebook_id) ?? ""} tagNames={tagNames} events={events} />
        ))}
      </tbody>
    </table>
  );
}

function TopRow({ note, selected, checked, starred, notebook, tagNames, events }: { note: NoteRow; selected: boolean; checked: boolean; starred: boolean; notebook: string; tagNames: Map<string, string>; events: CardEvents }) {
  const handlers = useCardEvents(note.id, events);
  const tagIds: string[] = parseTagIds(note.tag_ids);
  return (
    <tr role="option" aria-selected={selected || checked} className={`${selected ? "selected" : ""}${checked ? " checked" : ""}`} {...handlers}>
      <td className="col-title">
        {note.pinned_at != null && <Pin size={11} className="pin" />}
        {note.title || "Untitled"}
        {starred && <Star size={12} className="star" aria-label="Shortcut" />}
      </td>
      <td className="col-date">{shortDate(note.updated)}</td>
      <td className="col-date">{shortDate(note.created)}</td>
      <td className="col-nb">{notebook}</td>
      <td className="col-tags">{tagIds.map((t) => tagNames.get(t)).filter(Boolean).join(", ")}</td>
    </tr>
  );
}
